import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Inject,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  WalletService,
  WalletStatusTypeEnum,
  AccountWalletDetails,
  CustomerService,
  UserService,
  PermissionNameEnum,
} from '@cai-services';
import { concatMap, Subject, Subscription, takeUntil } from 'rxjs';
import { AppWalletService } from '../../core/_services/app-wallet.service';
import { SessionService } from '../../core/_services/session.service';
import { ApplicationProperties, Office } from '../../core';
import { APP_PROPS } from '../../cai-common.module';

@Component({
  selector: 'kt-cargo-wallet-management',
  templateUrl: './cargo-wallet-management.component.html',
  styleUrls: ['./cargo-wallet-management.component.scss'],
  standalone: false,
})
export class CargoWalletManagementComponent implements OnInit, OnDestroy {
  WalletTypeEnum = WalletStatusTypeEnum;
  public walletStatus: WalletStatusTypeEnum = WalletStatusTypeEnum.INACTIVE;
  walletDetails: AccountWalletDetails;
  walletDetailsPaylater: AccountWalletDetails;
  showActivationForm = false;
  isPageReady = false;
  walletLoader = true;
  canViewBalance: boolean = true;
  selectedOffice: Office;
  private officeChangedStatusSub: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly sessionService: SessionService,
    private readonly walletService: WalletService,
    private readonly customerService: CustomerService,
    private readonly ref: ChangeDetectorRef,
    private readonly appWalletService: AppWalletService,
    private readonly userService: UserService,
    public titleService: Title,
  ) {}

  async ngOnInit(): Promise<void> {
    document.getElementsByTagName('body')[0].classList.add('wallet-bg');
    const user = this.sessionService.getCurrentUser();

    this.officeChangedStatusSub = this.sessionService.officeChangedStatusObs
      .pipe(
        takeUntil(this.destroy$),
        concatMap(async (_) => {
          this.selectedOffice = this.sessionService.getSelectedOffice();
          this.canViewBalance = this.userService.hasPermission(
            user,
            this.selectedOffice?.id,
            PermissionNameEnum.READ_BALANCE,
          );
          if (!this.canViewBalance) {
            this.walletDetails = {} as AccountWalletDetails;
            this.walletDetails.balances = [
              {
                amount: 0,
                currency: 'SGD',
              },
              {
                amount: 0,
                currency: 'USD',
              },
            ];
          }
          await this.getWalletAccountDetails();
        }),
      )
      .subscribe();
    this.titleService.setTitle('My Office Wallet');
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.getElementsByTagName('body')[0].classList.remove('wallet-bg');
    if (this.officeChangedStatusSub) {
      this.officeChangedStatusSub.unsubscribe();
    }
  }

  async getWalletAccountDetails(): Promise<void> {
    this.walletDetails = {} as AccountWalletDetails;
    try {
      this.isPageReady = false;
      this.walletLoader = true;
      const officeId = this.selectedOffice?.id?.toString(),
        status = this.selectedOffice?.walletStatus;
      if (
        officeId &&
        status === WalletStatusTypeEnum.ACTIVE &&
        this.selectedOffice?.walletId
      ) {
        await this.initActiveWallet(this.selectedOffice);
      } else {
        this.walletStatus = status || WalletStatusTypeEnum.INACTIVE;
        this.walletDetails = undefined;
        this.walletDetailsPaylater = undefined;
      }
      if (officeId) {
        this.isPageReady = true;
        this.walletLoader = false;
      }
      this.ref.detectChanges();
    } catch (error) {
      console.error(error);
      this.isPageReady = true;
      this.walletLoader = false;
      this.ref.detectChanges();
    }
  }

  async initActiveWallet(office: Office): Promise<void> {
    try {
      const officeId = office.id.toString();
      let getWalletPayLaterBalances$;
      let getAccountWalletDetails$;
      if (this.isCargoWallet) {
        getAccountWalletDetails$ = this.customerService.getAccountWalletDetails(
          office.walletId,
        );
      } else {
        getAccountWalletDetails$ =
          this.walletService.getAccountWalletDetails(officeId);
      }
      if (this.isCargoWallet) {
        getWalletPayLaterBalances$ =
          this.customerService.getWalletPayLaterBalances(office.walletId);
      } else {
        getWalletPayLaterBalances$ =
          this.walletService.getWalletPayLaterBalances(officeId);
      }
      const payLaterDefaultBalance = await getWalletPayLaterBalances$,
        paylaterBalance = (
          payLaterDefaultBalance.limit - +payLaterDefaultBalance.future_movement
        ).toFixed(2);

      const account = await getAccountWalletDetails$;
      this.walletDetails = account;
      this.walletDetailsPaylater = { ...account };
      this.walletDetailsPaylater.balances = [
        {
          amount: +paylaterBalance,
          currency: account.default_pay_later_currency,
        },
      ];
      this.walletStatus = account.status;
      this.orderCurrencies();
    } catch (error) {
      console.error(error);
      this.walletStatus = this.selectedOffice.walletStatus;
    }
  }
  showWalletActivationForm(): void {
    this.showActivationForm = true;
  }
  get walletStatusDescription(): string {
    switch (this.walletStatus) {
      case WalletStatusTypeEnum.ACTIVE:
        return $localize`:@@wallet.activated-status-description:It seems that you have no money available in your Office Wallet. Please click on the Top Up button to top up your Office Wallet.`;
      case WalletStatusTypeEnum.INACTIVE:
        return $localize`:@@wallet.inactive-status-description:Your current CargoWALLET is Inactive. Kindly click on Activate my wallet to send us your application.`;
      case WalletStatusTypeEnum.PENDING:
        return $localize`:@@wallet.pending-status-description:It seems that you have asked us to activate your Wallet. Our team is currently reviewing your application.`;
      case WalletStatusTypeEnum.BLOCKED:
        return $localize`:@@wallet.disabled-status-description:We have temporarily blocked your office's CargoWALLET. Please reach our team at support+wallet@cargoai.co to know more about it.`;
    }
    return $localize`:@@wallet.invalid-status-description:Your current prepaid wallet is not active. To activate click the button and follow the instructions`;
  }
  setDefaultCurrency(value: string) {
    this.walletDetails.default_currency = value;
    this.appWalletService.updateDefaultCurrency(value);
    this.orderCurrencies();
    this.ref.detectChanges();
  }
  orderCurrencies() {
    this.walletDetails.balances = this.walletDetails.balances
      .filter(
        (balance) => balance.currency == this.walletDetails.default_currency,
      )
      .concat(
        this.walletDetails.balances.filter(
          (balance) => balance.currency != this.walletDetails.default_currency,
        ),
      );
  }

  get isCargoWallet(): boolean {
    return this.appProperties.name === 'cargowallet';
  }
}
